import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { db } from '@/config/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || (import.meta && import.meta.env && import.meta.env.REACT_APP_BACKEND_URL);

export default function TournamentNew() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);

  // Form state
  const [name, setName] = useState('');
  const [courts, setCourts] = useState(2);
  const [roundDuration, setRoundDuration] = useState(90);
  const [breakDuration, setBreakDuration] = useState(5);
  const [totalDuration, setTotalDuration] = useState(90);
  const [homeAdvantage, setHomeAdvantage] = useState(false);
  const [players, setPlayers] = useState([]);
  const [newPlayerName, setNewPlayerName] = useState('');
  const [bulkPlayers, setBulkPlayers] = useState('');
  const [showUserSelect, setShowUserSelect] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  // Load users when entering step 2
  useEffect(() => {
    if (step === 2) {
      loadUsers();
    }
  }, [step]);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersData = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAvailableUsers(usersData);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const toggleUserSelection = (userId) => {
    if (selectedUserIds.includes(userId)) {
      setSelectedUserIds(selectedUserIds.filter(id => id !== userId));
    } else {
      setSelectedUserIds([...selectedUserIds, userId]);
    }
  };

  const addSelectedUsers = () => {
    const newPlayers = availableUsers
      .filter(user => selectedUserIds.includes(user.id))
      .filter(user => !players.find(p => p.user_id === user.id)) // Keine Duplikate
      .map(user => ({
        name: user.display_name || user.name || user.email,
        user_id: user.id
      }));
    
    setPlayers([...players, ...newPlayers]);
    setSelectedUserIds([]);
    setShowUserSelect(false);
  };

  const addPlayer = () => {
    if (newPlayerName.trim()) {
      setPlayers([...players, { name: newPlayerName.trim() }]);
      setNewPlayerName('');
    }
  };

  const addBulkPlayers = () => {
    const names = bulkPlayers.split('\n').map(n => n.trim()).filter(n => n);
    const newPlayers = names.map(name => ({ name }));
    setPlayers([...players, ...newPlayers]);
    setBulkPlayers('');
  };

  const removePlayer = (index) => {
    setPlayers(players.filter((_, i) => i !== index));
  };

  const createTournament = async () => {
    if (!name.trim()) {
      alert('Bitte Turniername eingeben!');
      return;
    }

    if (players.length < 4) {
      alert('Mindestens 4 Spieler erforderlich!');
      return;
    }

    setLoading(true);

    try {
      // Create tournament
      const tournamentResponse = await fetch(`${BACKEND_URL}/api/tournaments/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          courts,
          round_duration_seconds: roundDuration * 60,
          break_duration_seconds: breakDuration * 60,
          total_duration_minutes: totalDuration,
          home_advantage_enabled: homeAdvantage,
          owner_id: user.uid
        })
      });

      const tournamentData = await tournamentResponse.json();

      if (!tournamentData.ok) {
        throw new Error(tournamentData.message || 'Tournament creation failed');
      }

      const tournamentId = tournamentData.tournament_id;

      // Add players in bulk (1 request instead of N requests)
      await fetch(`${BACKEND_URL}/api/tournaments/${tournamentId}/players/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(players.map(p => ({ 
          name: p.name, 
          user_id: p.user_id || null 
        })))
      });

      alert('✅ Turnier erfolgreich erstellt!');
      
      // Small delay to ensure Firestore consistency
      setTimeout(() => {
        navigate(`/tournaments/${tournamentId}`);
      }, 500);
    } catch (error) {
      console.error('Failed to create tournament:', error);
      alert(`❌ Fehler: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0d0409] text-rose-100 p-4 pb-20">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button 
            onClick={() => navigate('/tournaments')}
            variant="ghost"
            className="text-rose-200"
          >
            ← Zurück
          </Button>
          <h1 className="text-2xl font-bold text-rose-200">Neues Turnier erstellen</h1>
        </div>

        {/* Step Indicator */}
        <div className="flex gap-2 mb-6">
          {[1, 2, 3].map(s => (
            <div 
              key={s} 
              className={`flex-1 h-2 rounded ${
                s <= step ? 'bg-[#b40023]' : 'bg-white/10'
              }`}
            />
          ))}
        </div>

        {/* Step 1: Grunddaten */}
        {step === 1 && (
          <Card className="bg-[#1a0d11] border-white/10">
            <CardContent className="pt-6 space-y-4">
              <h2 className="text-xl font-semibold text-rose-200 mb-4">Schritt 1: Grunddaten</h2>

              <div>
                <label className="block text-sm text-rose-200/70 mb-1">Turniername *</label>
                <input 
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="z.B. Padel Mixer Dezember 2025"
                  className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-rose-100"
                />
              </div>

              <div>
                <label className="block text-sm text-rose-200/70 mb-1">Anzahl Plätze</label>
                <select 
                  value={courts}
                  onChange={e => setCourts(parseInt(e.target.value))}
                  className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-rose-100"
                >
                  <option value={1}>1 Platz (4 Spieler)</option>
                  <option value={2}>2 Plätze (8 Spieler)</option>
                  <option value={3}>3 Plätze (12 Spieler)</option>
                  <option value={4}>4 Plätze (16 Spieler)</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-rose-200/70 mb-1">Rundendauer (Minuten)</label>
                  <input 
                    type="number"
                    step="1"
                    min="1"
                    value={roundDuration}
                    onChange={e => setRoundDuration(parseInt(e.target.value) || 0)}
                    placeholder="z.B. 90"
                    className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-rose-100"
                  />
                  <p className="text-xs text-rose-200/40 mt-1">Exakte Minuten (z.B. 90 für 1:30h)</p>
                </div>
                <div>
                  <label className="block text-sm text-rose-200/70 mb-1">Pausendauer (Minuten)</label>
                  <input 
                    type="number"
                    step="1"
                    min="1"
                    value={breakDuration}
                    onChange={e => setBreakDuration(parseInt(e.target.value) || 0)}
                    placeholder="z.B. 5"
                    className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-rose-100"
                  />
                  <p className="text-xs text-rose-200/40 mt-1">Exakte Minuten</p>
                </div>
              </div>

              <div>
                <label className="block text-sm text-rose-200/70 mb-1">Gesamtzeit des Turniers (Minuten) *</label>
                <input 
                  type="number"
                  step="1"
                  min="1"
                  value={totalDuration}
                  onChange={e => setTotalDuration(parseInt(e.target.value) || 0)}
                  placeholder="z.B. 90"
                  className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-rose-100"
                />
                <p className="text-xs text-rose-200/40 mt-1">
                  Wie viel Zeit habt ihr insgesamt? (z.B. 90 Minuten)
                </p>
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox"
                    checked={homeAdvantage}
                    onChange={e => setHomeAdvantage(e.target.checked)}
                    className="w-5 h-5 rounded"
                  />
                  <span className="text-sm text-rose-200">
                    🏠 Heimrecht aktivieren
                  </span>
                </label>
                <p className="text-xs text-rose-200/40 mt-1 ml-7">
                  Teams mit Heimrecht werden markiert und fair verteilt
                </p>
              </div>

              <Button 
                onClick={() => setStep(2)}
                disabled={!name.trim()}
                className="w-full bg-[#b40023] hover:bg-[#8b0019]"
              >
                Weiter zu Schritt 2
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Teilnehmer */}
        {step === 2 && (
          <Card className="bg-[#1a0d11] border-white/10">
            <CardContent className="pt-6 space-y-4">
              <h2 className="text-xl font-semibold text-rose-200 mb-4">Schritt 2: Teilnehmer</h2>

              {/* User aus App auswählen */}
              <div>
                <label className="block text-sm text-rose-200/70 mb-1">👥 Spieler aus App-Nutzern auswählen</label>
                {!showUserSelect ? (
                  <Button 
                    onClick={() => setShowUserSelect(true)}
                    className="w-full bg-blue-700 hover:bg-blue-800"
                  >
                    App-Nutzer hinzufügen
                  </Button>
                ) : (
                  <div className="space-y-2">
                    {loadingUsers ? (
                      <p className="text-rose-200/50 text-center py-4">Lade Nutzer...</p>
                    ) : (
                      <>
                        <div className="max-h-60 overflow-y-auto space-y-1 bg-white/5 p-3 rounded">
                          {availableUsers.map(user => {
                            const isAlreadyAdded = players.find(p => p.user_id === user.id);
                            const isSelected = selectedUserIds.includes(user.id);
                            
                            return (
                              <div 
                                key={user.id} 
                                onClick={() => !isAlreadyAdded && toggleUserSelection(user.id)}
                                className={`p-2 rounded cursor-pointer flex items-center gap-2 ${
                                  isAlreadyAdded ? 'bg-green-900/30 opacity-50 cursor-not-allowed' : 
                                  isSelected ? 'bg-blue-600/40' : 'bg-white/10 hover:bg-white/20'
                                }`}
                              >
                                <input 
                                  type="checkbox"
                                  checked={isSelected || isAlreadyAdded}
                                  disabled={isAlreadyAdded}
                                  onChange={() => {}}
                                  className="w-4 h-4"
                                />
                                <span className="flex-1">{user.display_name || user.name || user.email}</span>
                                {isAlreadyAdded && <span className="text-green-400">✓ Bereits hinzugefügt</span>}
                              </div>
                            );
                          })}
                        </div>
                        
                        <div className="flex gap-2">
                          <Button 
                            onClick={() => {
                              setShowUserSelect(false);
                              setSelectedUserIds([]);
                            }}
                            variant="outline"
                            className="flex-1"
                          >
                            Abbrechen
                          </Button>
                          <Button 
                            onClick={addSelectedUsers}
                            disabled={selectedUserIds.length === 0}
                            className="flex-1 bg-green-700 hover:bg-green-800"
                          >
                            {selectedUserIds.length} Spieler hinzufügen
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className="text-center text-rose-200/40 text-sm">oder</div>

              {/* Einzeln hinzufügen */}
              <div>
                <label className="block text-sm text-rose-200/70 mb-1">Manuell Spieler hinzufügen</label>
                <div className="flex gap-2">
                  <input 
                    type="text"
                    value={newPlayerName}
                    onChange={e => setNewPlayerName(e.target.value)}
                    onKeyPress={e => e.key === 'Enter' && addPlayer()}
                    placeholder="Name"
                    className="flex-1 bg-white/5 border border-white/10 rounded px-3 py-2 text-rose-100"
                  />
                  <Button onClick={addPlayer} className="bg-green-700 hover:bg-green-800">
                    +
                  </Button>
                </div>
              </div>

              {/* Bulk hinzufügen */}
              <div>
                <label className="block text-sm text-rose-200/70 mb-1">Oder mehrere auf einmal (eine Zeile = ein Spieler)</label>
                <textarea 
                  value={bulkPlayers}
                  onChange={e => setBulkPlayers(e.target.value)}
                  placeholder="Max Mustermann\nErika Musterfrau\n..."
                  rows={4}
                  className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-rose-100"
                />
                <Button onClick={addBulkPlayers} className="mt-2 bg-blue-700 hover:bg-blue-800">
                  Alle hinzufügen
                </Button>
              </div>

              {/* Players List */}
              {players.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-rose-200/70 mb-2">
                    Teilnehmer ({players.length})
                  </h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {players.map((player, index) => (
                      <div key={index} className="flex justify-between items-center bg-white/5 rounded px-3 py-2">
                        <span className="text-rose-100">{player.name}</span>
                        <button 
                          onClick={() => removePlayer(index)}
                          className="text-red-400 hover:text-red-300"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <Button 
                  onClick={() => setStep(1)}
                  variant="outline"
                  className="flex-1"
                >
                  Zurück
                </Button>
                <Button 
                  onClick={() => setStep(3)}
                  disabled={players.length < 4}
                  className="flex-1 bg-[#b40023] hover:bg-[#8b0019]"
                >
                  Weiter zu Schritt 3
                </Button>
              </div>

              {players.length < 4 && (
                <p className="text-xs text-orange-400 text-center">
                  Mindestens 4 Spieler erforderlich
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 3: Vorschau & Erstellen */}
        {step === 3 && (
          <Card className="bg-[#1a0d11] border-white/10">
            <CardContent className="pt-6 space-y-4">
              <h2 className="text-xl font-semibold text-rose-200 mb-4">Schritt 3: Zusammenfassung</h2>

              <div className="space-y-3 text-sm">
                <div>
                  <span className="text-rose-200/70">Name:</span>
                  <p className="text-rose-100 font-semibold">{name}</p>
                </div>
                <div>
                  <span className="text-rose-200/70">Format:</span>
                  <p className="text-rose-100">Padel Mixer (Zeitbasiert)</p>
                </div>
                <div>
                  <span className="text-rose-200/70">Plätze:</span>
                  <p className="text-rose-100">{courts} Plätze ({courts * 4} Spieler gleichzeitig)</p>
                </div>
                <div>
                  <span className="text-rose-200/70">Rundendauer:</span>
                  <p className="text-rose-100">{roundDuration} Minuten</p>
                </div>
                <div>
                  <span className="text-rose-200/70">Pausendauer:</span>
                  <p className="text-rose-100">{breakDuration} Minuten</p>
                </div>
                <div>
                  <span className="text-rose-200/70">Gesamtzeit:</span>
                  <p className="text-rose-100 font-semibold">{totalDuration} Minuten</p>
                  <p className="text-xs text-rose-200/50 mt-1">
                    ≈ {Math.floor(totalDuration / ((roundDuration + breakDuration)))} Runden möglich
                  </p>
                </div>
                {homeAdvantage && (
                  <div>
                    <span className="text-rose-200/70">Heimrecht:</span>
                    <p className="text-rose-100">✅ Aktiviert (wird fair verteilt)</p>
                  </div>
                )}
                <div>
                  <span className="text-rose-200/70">Teilnehmer:</span>
                  <p className="text-rose-100">{players.length} Spieler</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {players.map((player, i) => (
                      <span key={i} className="bg-white/10 px-2 py-1 rounded text-xs">
                        {player.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="border-t border-white/10 pt-4 mt-4">
                <p className="text-sm text-rose-200/60 mb-4">
                  🎾 Nach Erstellung kannst du das Turnier starten und die Runden werden automatisch generiert.
                </p>
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={() => setStep(2)}
                  variant="outline"
                  className="flex-1"
                >
                  Zurück
                </Button>
                <Button 
                  onClick={createTournament}
                  disabled={loading}
                  className="flex-1 bg-green-700 hover:bg-green-800"
                >
                  {loading ? 'Erstelle...' : '✅ Turnier erstellen'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}